const API_BASE_URL = import.meta.env.VITE_API_URL || "";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

// Token storage keys - separated to avoid conflicts
const TOKEN_KEYS = {
  advisor: "advisor_auth_token",
  admin: "advisor_panel_admin_token",
} as const;

type TokenContext = keyof typeof TOKEN_KEYS;

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private tokenContext: TokenContext = "advisor";

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Try to restore advisor token from localStorage by default
    this.token = localStorage.getItem(TOKEN_KEYS.advisor);
  }

  setTokenContext(context: TokenContext) {
    this.tokenContext = context;
    this.token = localStorage.getItem(TOKEN_KEYS[context]);
  }

  getTokenContext(): TokenContext {
    return this.tokenContext;
  }

  setToken(token: string | null, context?: TokenContext) {
    const ctx = context || this.tokenContext;
    this.token = token;
    if (token) {
      localStorage.setItem(TOKEN_KEYS[ctx], token);
    } else {
      localStorage.removeItem(TOKEN_KEYS[ctx]);
    }
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { body, headers: customHeaders, ...rest } = options;

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...customHeaders,
    };

    if (this.token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...rest,
      headers,
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || errorData.error || "Request failed");
      (error as Error & { status: number }).status = response.status;
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request<{ token: string; advisor: unknown }>("/api/advisors/login", {
      method: "POST",
      body: { email, password },
    });
  }

  async register(data: {
    email: string;
    password: string;
    name: string;
    company?: string;
    phone?: string;
    // Role distinction fields
    roleType?: "doradca" | "ekspert" | "oba";
    rpkNumber?: string;
    rpkDivision?: "I" | "II" | "I_II";
    compensationModel?: "client" | "bank" | "mixed";
    nip?: string;
  }) {
    return this.request<{ success: boolean; message: string; advisor: unknown }>("/api/advisors/register", {
      method: "POST",
      body: data,
    });
  }

  async adminLogin(email: string, password: string) {
    return this.request<{ token: string; admin: unknown }>("/api/admin/login", {
      method: "POST",
      body: { email, password },
    });
  }

  // Advisor profile
  async getProfile() {
    return this.request<{ advisor: unknown }>("/api/advisor/profile");
  }

  async updateProfile(data: {
    name?: string;
    company?: string;
    phone?: string;
    // Role distinction fields
    roleType?: "doradca" | "ekspert" | "oba";
    rpkNumber?: string;
    rpkDivision?: "I" | "II" | "I_II";
    compensationModel?: "client" | "bank" | "mixed";
    nip?: string;
  }) {
    return this.request<{ advisor: unknown }>("/api/advisor/profile", {
      method: "PUT",
      body: data,
    });
  }

  // Leads
  async getLeads() {
    return this.request<{ leads: unknown[] }>("/api/advisor/leads");
  }

  async reserveLead(leadId: string) {
    return this.request<unknown>(`/api/advisor/leads/${leadId}/reserve`, {
      method: "POST",
    });
  }

  // Sessions
  async getSessions() {
    return this.request<{ sessions: unknown[] }>("/api/advisor/sessions");
  }

  async getSession(sessionId: string) {
    return this.request<unknown>(`/api/advisor/sessions/${sessionId}`);
  }

  async updateSession(sessionId: string, data: { status?: string; advisorNotes?: string }) {
    return this.request<unknown>(`/api/advisor/sessions/${sessionId}`, {
      method: "PATCH",
      body: data,
    });
  }

  async updateSessionNotes(sessionId: string, notes: string) {
    return this.request<{ session: { advisorNotes: string; updatedAt: string } }>(`/api/advisor/sessions/${sessionId}`, {
      method: "PATCH",
      body: { advisorNotes: notes },
    });
  }

  async startSession(sessionId: string) {
    return this.request<unknown>(`/api/advisor/sessions/${sessionId}/start`, {
      method: "POST",
    });
  }

  // Offers
  async createOffer(sessionId: string, data: unknown) {
    return this.request<unknown>(`/api/advisor/sessions/${sessionId}/offers`, {
      method: "POST",
      body: data,
    });
  }

  async updateOffer(sessionId: string, offerId: string, data: unknown) {
    return this.request<unknown>(`/api/advisor/sessions/${sessionId}/offers/${offerId}`, {
      method: "PUT",
      body: data,
    });
  }

  async deleteOffer(sessionId: string, offerId: string) {
    return this.request<unknown>(`/api/advisor/sessions/${sessionId}/offers/${offerId}`, {
      method: "DELETE",
    });
  }

  // Admin endpoints
  async getAdvisors() {
    return this.request<{ advisors: unknown[] }>("/api/admin/advisors");
  }

  async verifyAdvisor(advisorId: string) {
    return this.request<unknown>(`/api/admin/advisors/${advisorId}/verify`, {
      method: "POST",
    });
  }

  async suspendAdvisor(advisorId: string, reason?: string) {
    return this.request<unknown>(`/api/admin/advisors/${advisorId}/suspend`, {
      method: "POST",
      body: { reason },
    });
  }

  async verifyRpk(advisorId: string, rpkVerified: boolean, note?: string) {
    return this.request<{ success: boolean; message: string; advisor: unknown }>(
      `/api/admin/advisors/${advisorId}/verify-rpk`,
      {
        method: "PUT",
        body: { rpkVerified, note },
      }
    );
  }

  async assignCredits(advisorId: string, amount: number, reason?: string) {
    return this.request<unknown>(`/api/admin/advisors/${advisorId}/credits`, {
      method: "POST",
      body: { amount, reason },
    });
  }

  async getStats() {
    return this.request<unknown>("/api/admin/stats");
  }

  async getAuditLogs(params?: {
    action?: string;
    actorId?: string;
    actorType?: "advisor" | "admin" | "system";
    startDate?: string;
    endDate?: string;
    limit?: number;
    cursor?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.action) searchParams.set("action", params.action);
    if (params?.actorId) searchParams.set("actorId", params.actorId);
    if (params?.actorType) searchParams.set("actorType", params.actorType);
    if (params?.startDate) searchParams.set("startDate", params.startDate);
    if (params?.endDate) searchParams.set("endDate", params.endDate);
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.cursor) searchParams.set("cursor", params.cursor);

    const queryString = searchParams.toString();
    return this.request<{ logs: unknown[]; count: number; nextCursor?: string }>(
      `/api/admin/audit-logs${queryString ? `?${queryString}` : ""}`
    );
  }

  // Offer Templates
  async getTemplates() {
    return this.request<{ templates: unknown[] }>("/api/advisor/templates");
  }

  async createTemplate(data: {
    name: string;
    bankName: string;
    productName: string;
    type: string;
    defaultPeriod: number;
    defaultInterestRate: number;
    defaultRrso?: number;
    defaultCommission?: number;
    defaultMargin?: number;
    notes?: string;
    isDefault?: boolean;
  }) {
    return this.request<{ template: unknown }>("/api/advisor/templates", {
      method: "POST",
      body: data,
    });
  }

  async updateTemplate(templateId: string, data: {
    name?: string;
    bankName?: string;
    productName?: string;
    type?: string;
    defaultPeriod?: number;
    defaultInterestRate?: number;
    defaultRrso?: number;
    defaultCommission?: number;
    defaultMargin?: number;
    notes?: string;
    isDefault?: boolean;
  }) {
    return this.request<{ template: unknown }>(`/api/advisor/templates/${templateId}`, {
      method: "PUT",
      body: data,
    });
  }

  async deleteTemplate(templateId: string) {
    return this.request<unknown>(`/api/advisor/templates/${templateId}`, {
      method: "DELETE",
    });
  }

  // Public Profile
  async getPublicProfile() {
    return this.request<{ publicProfile: unknown }>("/api/advisor/public-profile");
  }

  async updatePublicProfile(data: {
    isPublic?: boolean;
    slug?: string;
    headline?: string;
    bio?: string;
    photoUrl?: string;
    yearsOfExperience?: number;
    clientsServed?: number;
    creditsFinalized?: number;
    specializations?: string[];
    targetClients?: string[];
    regions?: string[];
    certificates?: Array<{
      name: string;
      issuedBy: string;
      year: number;
      isVerified?: boolean;
    }>;
    partnerBanks?: string[];
  }) {
    return this.request<{ publicProfile: unknown }>("/api/advisor/public-profile", {
      method: "PUT",
      body: data,
    });
  }

  async getContactPreferences() {
    return this.request<{ contactPreferences: unknown }>("/api/advisor/contact-preferences");
  }

  async updateContactPreferences(data: {
    preferredContactMethod?: "phone" | "email" | "both";
    availableHours?: {
      weekdays: { from: string; to: string };
      saturday?: { from: string; to: string };
      sunday?: { from: string; to: string };
    };
    timezone?: string;
    responseTime?: string;
  }) {
    return this.request<{ contactPreferences: unknown }>("/api/advisor/contact-preferences", {
      method: "PUT",
      body: data,
    });
  }

  async checkSlugAvailability(slug: string) {
    return this.request<{ available: boolean }>(`/api/advisor/public-profile/check-slug?slug=${encodeURIComponent(slug)}`);
  }

  // Public endpoint (no auth) - for viewing advisor profiles
  async getAdvisorPublicProfile(slug: string) {
    return this.request<{ advisor: unknown; profile: unknown }>(`/api/advisors/public/${slug}`);
  }

  // Consent-Based Data Sharing
  async getSessionConsent(sessionToken: string) {
    return this.request<{
      consent: {
        status: "pending" | "granted" | "revoked" | "expired";
        sharedScopes?: {
          credits: boolean;
          scoring: boolean;
          creditworthiness: boolean;
          personalData: boolean;
        };
        grantedAt?: string;
        expiresAt?: string;
      };
      sharedData?: {
        credits?: unknown[];
        scoring?: unknown;
        creditworthiness?: unknown;
        personalData?: unknown;
        lastSyncedAt?: string;
        lastEditedBy?: "client" | "advisor";
        version: number;
      };
    }>(`/api/sessions/${sessionToken}/consent`);
  }

  async updateSharedData(
    sessionToken: string,
    data: {
      credits?: unknown[];
      scoring?: unknown;
      creditworthiness?: unknown;
      personalData?: unknown;
    },
    version: number
  ) {
    return this.request<{
      sharedData: {
        credits?: unknown[];
        scoring?: unknown;
        creditworthiness?: unknown;
        personalData?: unknown;
        lastSyncedAt: string;
        lastEditedBy: "advisor";
        version: number;
      };
    }>(`/api/sessions/${sessionToken}/shared-data`, {
      method: "PATCH",
      body: { ...data, version },
    });
  }

  // Tickets (Bug Reports)
  async createTicket(data: {
    description: string;
    context: {
      pageUrl: string;
      browserInfo: string;
      screenResolution: string;
      userAgent: string;
    };
  }) {
    return this.request<{ ticket: { id: string; status: string } }>("/api/advisor/tickets", {
      method: "POST",
      body: data,
    });
  }

  async getTickets(params?: {
    status?: string[];
    page?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.status?.length) searchParams.set("status", params.status.join(","));
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());

    const queryString = searchParams.toString();
    return this.request<{
      tickets: unknown[];
      total: number;
      page: number;
      limit: number;
    }>(`/api/advisor/tickets${queryString ? `?${queryString}` : ""}`);
  }

  async getTicket(ticketId: string) {
    return this.request<{ ticket: unknown }>(`/api/advisor/tickets/${ticketId}`);
  }
}

export const api = new ApiClient(API_BASE_URL);
